import { useState } from "react";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  PartyPopper,
  Gift,
  CreditCard,
  Palette,
  Star,
  ClipboardList,
  Camera,
  Mail
} from "lucide-react";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import BirthdayReservationForm from "@/components/BirthdayReservationForm";
import { motion } from "framer-motion";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Cumpleaños en Gándara | Celebra en la naturaleza",
    description: "Celebra el cumpleaños de tu hijo en 5.000m² de naturaleza en Gondomar. Fiestas temáticas Montessori con actividades al aire libre.",
  },
  en: {
    title: "Birthdays at Gándara | Celebrate in Nature",
    description: "Celebrate your child's birthday in 5,000m² of nature in Gondomar. Montessori-themed parties with outdoor activities.",
  },
  pt: {
    title: "Aniversários na Gándara | Celebre na Natureza",
    description: "Celebre o aniversário do seu filho em 5.000m² de natureza em Gondomar. Festas temáticas Montessori com atividades ao ar livre.",
  },
  fr: {
    title: "Anniversaires à Gándara | Célébrez dans la Nature",
    description: "Célébrez l'anniversaire de votre enfant dans 5 000m² de nature à Gondomar. Fêtes thématiques Montessori avec activités en plein air.",
  },
};

const Birthdays = () => {
  const { language } = useLanguage();
  const t = translations[language].birthdays;
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />
      
      <div className={`${isBannerVisible ? 'pt-[120px]' : 'pt-20'} pb-20`}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <PartyPopper className="w-16 h-16 text-primary mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-light text-primary mb-4">
                {t.title}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-2">
                {t.subtitle}
              </p>
              <p className="text-base text-muted-foreground">
                {t.courseYear}
              </p>
            </div>

            {/* Gallery Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Camera className="w-8 h-8 text-primary" />
                {t.gallery.title}
              </h2>
              
              <div className="relative aspect-video rounded-xl overflow-hidden mb-4 bg-muted">
                <OptimizedPicture 
                  src={t.gallery.images[0].src}
                  alt={t.gallery.images[0].alt}
                  width={800}
                  height={450}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <OptimizedPicture 
                    src={t.gallery.images[1].src}
                    alt={t.gallery.images[1].alt}
                    width={400}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                </div>
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <OptimizedPicture 
                    src={t.gallery.images[2].src}
                    alt={t.gallery.images[2].alt}
                    width={400}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                </div>
              </div>
              
              <p className="text-center text-sm text-muted-foreground mt-4 italic">
                {t.gallery.caption}
              </p>
            </motion.div>

            {/* General Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-primary" />
                {t.generalInfo.title}
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p><strong className="text-primary">{t.generalInfo.availabilityLabel}:</strong> {t.generalInfo.availability}</p>
                <p><strong className="text-primary">{t.generalInfo.durationLabel}:</strong> {t.generalInfo.duration}</p>
                <p className="leading-relaxed">{t.generalInfo.description}</p>
              </div>
            </motion.div>

            {/* Available Plans Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Gift className="w-8 h-8 text-primary" />
                {t.plans.title}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* A tu Aire Plan */}
                <div className="border border-border rounded-xl p-6 hover:border-primary transition-colors">
                  <h3 className="text-xl font-semibold text-primary mb-2">{t.plans.aTuAire.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{t.plans.aTuAire.description}</p>
                  <ul className="space-y-2 mb-4">
                    {t.plans.aTuAire.features.map((feature: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5">✓</span>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-center pt-4 border-t border-border">
                    <p className="text-2xl font-bold text-primary">{t.plans.aTuAire.priceSmall}</p>
                    <p className="text-sm text-muted-foreground">{t.plans.priceNote}</p>
                  </div>
                </div>

                {/* Acompañada Plan */}
                <div className="border border-border rounded-xl p-6 hover:border-primary transition-colors">
                  <h3 className="text-xl font-semibold text-primary mb-2">{t.plans.acompanada.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{t.plans.acompanada.description}</p>
                  <ul className="space-y-2 mb-4">
                    {t.plans.acompanada.features.map((feature: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5">✓</span>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-center pt-4 border-t border-border">
                    <p className="text-2xl font-bold text-primary">{t.plans.acompanada.priceSmall}</p>
                    <p className="text-sm text-muted-foreground">{t.plans.priceNote}</p>
                  </div>
                </div>

                {/* Experiencia Completa Plan - Highlighted */}
                <div className="border-2 border-primary rounded-xl p-6 bg-accent/5 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground px-4 py-1 rounded-full text-sm font-medium">
                    {t.plans.experienciaCompleta.badge}
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-2 mt-2">{t.plans.experienciaCompleta.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{t.plans.experienciaCompleta.description}</p>
                  <ul className="space-y-2 mb-4">
                    {t.plans.experienciaCompleta.features.map((feature: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5">✓</span>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-center pt-4 border-t border-accent/30">
                    <p className="text-2xl font-bold text-primary">{t.plans.experienciaCompleta.priceSmall}</p>
                    <p className="text-sm text-muted-foreground">{t.plans.priceNote}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Pricing Table Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-primary" />
                {t.pricingTable.title}
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-primary font-medium">{t.pricingTable.headers.plan}</th>
                      <th className="text-center py-3 px-4 text-primary font-medium">{t.pricingTable.headers.small}</th>
                      <th className="text-center py-3 px-4 text-primary font-medium">{t.pricingTable.headers.large}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 text-muted-foreground">{t.plans.aTuAire.name}</td>
                      <td className="py-3 px-4 text-center font-semibold text-primary">{t.plans.aTuAire.priceSmall}</td>
                      <td className="py-3 px-4 text-center font-semibold text-primary">{t.plans.aTuAire.priceLarge}</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 text-muted-foreground">{t.plans.acompanada.name}</td>
                      <td className="py-3 px-4 text-center font-semibold text-primary">{t.plans.acompanada.priceSmall}</td>
                      <td className="py-3 px-4 text-center font-semibold text-primary">{t.plans.acompanada.priceLarge}</td>
                    </tr>
                    <tr className="bg-accent/5">
                      <td className="py-3 px-4 text-primary font-medium">{t.plans.experienciaCompleta.name}</td>
                      <td className="py-3 px-4 text-center font-semibold text-primary">{t.plans.experienciaCompleta.priceSmall}</td>
                      <td className="py-3 px-4 text-center font-semibold text-primary">{t.plans.experienciaCompleta.priceLarge}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Workshops Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Palette className="w-8 h-8 text-primary" />
                {t.workshops.title}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {t.workshops.items.map((workshop: string, index: number) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-background rounded-lg">
                    <span className="text-primary">✓</span>
                    <span className="text-muted-foreground">{workshop}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Extras Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Star className="w-8 h-8 text-primary" />
                {t.extras.title}
              </h2>
              
              <div className="space-y-3">
                {t.extras.items.map((extra: { name: string; price: string }, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-muted-foreground">{extra.name}</span>
                    <span className="font-semibold text-primary">{extra.price}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Booking Process Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <ClipboardList className="w-8 h-8 text-primary" />
                {t.booking.title}
              </h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
                  <h3 className="font-semibold text-primary mb-2">{t.booking.deposit}</h3>
                  <p className="text-muted-foreground text-sm">{t.booking.depositDescription}</p>
                </div>
                <div className="p-4 bg-background rounded-lg">
                  <h3 className="font-semibold text-primary mb-2">{t.booking.cancellation}</h3>
                  <p className="text-muted-foreground text-sm">{t.booking.cancellationDescription}</p>
                </div>
              </div>
            </motion.div>

            {/* Reservation Form Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <BirthdayReservationForm />
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 text-center"
            >
              <h2 className="text-2xl md:text-3xl font-light text-primary mb-4">
                {t.contact.title}
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                {t.contact.description}
              </p>
              <div className="flex justify-center">
                <Button
                  asChild
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-lg text-lg shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-xl flex items-center gap-3"
                >
                  <a href="mailto:info@gandaraschool.com">
                    <Mail className="w-5 h-5" />
                    {t.contact.emailButton}
                  </a>
                </Button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Birthdays;
